import { SectionBusinessId, BUSINESS_UNITS, Roles } from '@cambridgeassessment/lib-ee-commons';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Switch, useLocation } from 'react-router-dom';
import MalpracticeReports from './search';
import { RootState } from '../../../store';
import FailedReports from './components/FailedReports';
import MalpracticeReportsSummary from './components/MalpracticeReportsSummary/MalpracticeReportsSummary';
import ReportSummaryDetails from './components/ReportSummaryDetails';
import qs from 'qs';
import thunks from '../../../store/malpracticeReports/thunks';
import { SearchQuery } from '../../../types/Plagiarism';
import * as actions from '../../../store/malpracticeReports/actions';
import { Authorized } from 'lib-browser';

const Container: React.FC = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const {
    malpracticeReport,
    userData: { businessUnit, selectedRole },
  } = useSelector((state: RootState) => state);
  const { searchQuery, seriesData } = malpracticeReport;

  const isCI_BU = BUSINESS_UNITS.CIE.id === businessUnit?.id;

  const queryParams = useMemo(
    () =>
      qs.parse(location.search, { ignoreQueryPrefix: true }) as unknown as {
        searchQuery: Omit<SearchQuery, 'sectionId'>;
        low: number;
        high: number;
      },
    [location.search],
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    dispatch(thunks.getSeriesData(queryParams.searchQuery));
  }, [dispatch, queryParams.searchQuery]);

  useEffect(() => {
    if (queryParams.searchQuery) {
      dispatch(actions.setSearchQuery(queryParams.searchQuery));
    }
  }, [dispatch, location.search, queryParams.searchQuery]);

  const generateUrl = useCallback(
    (baseUrl: string, additionalQuery?: object) => {
      return `${baseUrl}?${qs.stringify({ searchQuery, ...additionalQuery })}`;
    },
    [searchQuery],
  );

  const fetchComponentDetails = useCallback(
    (componentToFetch: Omit<SectionBusinessId, 'centreId' | 'learnerNumber' | 'learnerUniqueId'>) => {
      dispatch(thunks.getReportDetailsSection(componentToFetch));
    },
    [dispatch],
  );

  return (
    <Switch>
      <Authorized
        selectedRole={selectedRole}
        roles={
          isCI_BU ? [Roles.SUBMIT_SUPER_ADMIN, Roles.SUBMIT_COMPLIANCE, Roles.SUBMIT_INTERNAL_ASSESSMENT_MANAGER] : []
        }
      >
        <Route
          exact
          strict
          path='/malpracticeReports'
          render={(props) => <MalpracticeReports {...props} series={seriesData} defaultSearchQuery={searchQuery} />}
        />
        <Route
          exact
          path='/malpracticeReports/:seriesId'
          render={(props) => (
            <MalpracticeReportsSummary {...props} generateUrl={generateUrl} searchQuery={queryParams.searchQuery} />
          )}
        />
        <Route
          exact
          path='/malpracticeReports/:seriesId/:assessmentCode/:componentNumber/:sectionId/details'
          render={(props) => (
            <ReportSummaryDetails {...props} queryParams={queryParams} fetchComponentDetails={fetchComponentDetails} />
          )}
        />

        <Route
          exact
          path='/malpracticeReports/:seriesId/:assessmentCode/:componentNumber/:sectionId/errors'
          render={(props) => (
            <FailedReports {...props} queryParams={queryParams} fetchComponentDetails={fetchComponentDetails} />
          )}
        />
      </Authorized>
    </Switch>
  );
};

export default Container;
